import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import DescriptionCell from "@/pages/DescriptionCell";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Calendar, Share2, Copy, ExternalLink, Pencil, Trash2, Plus, Info, Users, AlertCircle, Lock, Unlock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import TransactionModal from "@/pages/TransactionModal";
import PendingOrdersList from "@/pages/PendingOrdersList";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = { date: '', vendor_name: '', category: '', description: '', amount: '', deduction: '', receipt_link: '', notes: '' };

const HouseholdDashboard = ({ user }) => {
  const { configId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("weekly");
  const [refDate, setRefDate] = useState(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = day === 0 ? 6 : day - 1;
    now.setDate(now.getDate() - diff);
    return now.toISOString().split('T')[0];
  });
  const [viewMode, setViewMode] = useState('household'); // 'household' | 'member'
  const [selectedMember, setSelectedMember] = useState(null);
  const [pendingData, setPendingData] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [markingKey, setMarkingKey] = useState(null);
  const [showPaid, setShowPaid] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [form, setForm] = useState({ ...emptyForm });
  const [lineItems, setLineItems] = useState([]);
  const [editingLogId, setEditingLogId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Share
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  // Finalize month state
  const [togglingMonth, setTogglingMonth] = useState(false);
  const currentMonthStr = refDate ? refDate.slice(0, 7) : '';
  const closedMonths = data?.config?.closed_months || [];
  const isOwner = !!data?.config?.is_owner;
  const isCurrentMonthClosed = closedMonths.includes(currentMonthStr);

  const loadOrders = useCallback(async () => {
    if (period === 'pending') return; // pending uses its own loader
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/household/${configId}/orders`, {
        params: { period, ref_date: refDate }, withCredentials: true
      });
      setData(response.data);
      if (response.data.share_id) setShareUrl(`${window.location.origin}/shared/${response.data.share_id}`);
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [configId, period, refDate]);

  const loadPending = useCallback(async () => {
    setPendingLoading(true);
    try {
      const params = { months: 12, include_paid: showPaid };
      const splittingOn = !!data?.config?.bill_splitting_enabled;
      if (viewMode === 'member' && selectedMember) {
        params.member_id = selectedMember;
      } else if (!splittingOn) {
        // Non-split households: scope to virtual 'household' so we get a single mark-paid affordance.
        params.member_id = 'household';
      }
      const response = await axios.get(`${BACKEND_URL}/api/household/${configId}/pending`, {
        params, withCredentials: true
      });
      setPendingData(response.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load pending bills');
    } finally {
      setPendingLoading(false);
    }
  }, [configId, viewMode, selectedMember, showPaid, data?.config?.bill_splitting_enabled]);

  useEffect(() => { loadOrders(); }, [loadOrders]);
  useEffect(() => {
    if (period === 'pending') loadPending();
  }, [period, loadPending]);

  const splittingEnabled = !!data?.config?.bill_splitting_enabled;
  const members = data?.config?.members?.filter(m => m.is_active !== false) || [];
  const memberTotals = data?.member_totals || [];

  const navigateDate = (dir) => {
    const d = new Date(refDate);
    if (period === "weekly") d.setDate(d.getDate() + (dir === 'next' ? 7 : -7));
    else d.setMonth(d.getMonth() + (dir === 'next' ? 1 : -1));
    setRefDate(d.toISOString().split('T')[0]);
  };

  const handlePeriodChange = (p) => {
    setPeriod(p);
    if (p === 'pending') return;
    const now = new Date();
    if (p === "weekly") {
      const day = now.getDay();
      now.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
    } else { now.setDate(1); }
    setRefDate(now.toISOString().split('T')[0]);
  };

  const handleMarkMonth = async (period_, memberId, paid) => {
    const k = `${memberId}_${period_}`;
    setMarkingKey(k);
    try {
      await axios.post(
        `${BACKEND_URL}/api/household/${configId}/payments/mark`,
        { items: [{ member_id: memberId, period: period_, paid }] },
        { withCredentials: true }
      );
      toast.success(paid ? 'Marked paid' : 'Marked unpaid');
      await loadPending();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    } finally {
      setMarkingKey(null);
    }
  };

  // ---- MODAL ----
  const openAdd = () => {
    setModalMode('add');
    setForm({ ...emptyForm, date: new Date().toISOString().split('T')[0] });
    setLineItems([]);
    setEditingLogId(null);
    setModalOpen(true);
  };

  const openEdit = (order) => {
    setModalMode('edit');
    setForm({
      date: order.date || '', vendor_name: order.vendor_name || '', category: order.category || '',
      description: order.description || '', amount: String(order.amount || ''),
      deduction: String(order.deduction || ''), receipt_link: order.receipt_link || '',
      notes: order.notes || ''
    });
    setLineItems(
      (order.line_items || []).map(li => ({
        id: li.id || `li_${Math.random().toString(36).slice(2, 10)}`,
        description: li.description || "",
        amount: li.amount || 0,
        category: li.category || "",
        split_override: li.split_override || null,
        effective_split: li.effective_split || null,
      }))
    );
    setEditingLogId(order.log_id);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.date || !form.vendor_name) { toast.error('Date and vendor are required'); return; }
    setSaving(true);
    const payload = {
      ...form,
      amount: parseFloat(form.amount) || 0,
      deduction: parseFloat(form.deduction) || 0,
      line_items: lineItems.map(li => ({
        id: li.id,
        description: li.description,
        amount: parseFloat(li.amount) || 0,
        category: li.category || form.category || "",
        split_override: li.split_override || null,
      })),
    };
    try {
      if (modalMode === 'add') {
        await axios.post(`${BACKEND_URL}/api/household/${configId}/orders`, payload, { withCredentials: true });
        toast.success('Transaction added');
      } else {
        await axios.put(`${BACKEND_URL}/api/orders/${editingLogId}`, payload, { withCredentials: true });
        toast.success('Transaction updated');
      }
      setModalOpen(false);
      loadOrders();
    } catch (err) {
      const msg = err?.response?.status === 401
        ? 'Your session has expired. Please sign in again.'
        : (err?.response?.data?.detail || err?.message || 'Failed to save');
      toast.error(msg);
      if (err?.response?.status === 401) {
        setTimeout(() => { window.location.href = '/'; }, 1500);
      }
    }
    finally { setSaving(false); }
  };

  const deleteOrder = async (logId) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/orders/${logId}`, { withCredentials: true });
      toast.success('Deleted');
      loadOrders();
    } catch { toast.error('Failed to delete'); }
  };

  // ---- SHARE ----
  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success('Link copied!'); }
    catch {
      const ta = document.createElement('textarea'); ta.value = text; ta.style.cssText = 'position:fixed;left:-9999px';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); toast.success('Link copied!'); }
      catch { toast.info(text, { duration: 8000 }); }
      document.body.removeChild(ta);
    }
  };

  const periodSuffix = () => {
    if (period === 'monthly' && refDate) {
      const d = new Date(refDate);
      const names = ['january','february','march','april','may','june','july','august','september','october','november','december'];
      return `/${names[d.getMonth()]}-${d.getFullYear()}`;
    }
    if (period === 'weekly' && refDate) return `/week-${refDate}`;
    return '';
  };

  const ensureShareId = async () => {
    if (shareUrl) {
      const m = shareUrl.match(/\/([a-z0-9]{8,})(?:\/|$)/i);
      if (m) return m[1];
    }
    const res = await axios.post(`${BACKEND_URL}/api/household/${configId}/share`, {}, { withCredentials: true });
    return res.data.share_id;
  };

  const handleShareHousehold = async () => {
    setSharing(true);
    try {
      const sid = await ensureShareId();
      const url = `${window.location.origin}/shared/${sid}${periodSuffix()}`;
      setShareUrl(`${window.location.origin}/shared/${sid}`);
      await copyToClipboard(url);
    } catch { toast.error('Failed to create share link'); }
    finally { setSharing(false); }
  };

  const handleShareMember = async (memberId) => {
    setSharing(true);
    try {
      const sid = await ensureShareId();
      const url = `${window.location.origin}/shared/${sid}/member/${memberId}${periodSuffix()}`;
      await copyToClipboard(url);
    } catch { toast.error('Failed to create member link'); }
    finally { setSharing(false); }
  };

  const handleShareMemberPending = async (memberId) => {
    setSharing(true);
    try {
      const sid = await ensureShareId();
      const url = `${window.location.origin}/shared/${sid}/member/${memberId}/pending`;
      await copyToClipboard(url);
    } catch { toast.error('Failed to create pending link'); }
    finally { setSharing(false); }
  };

  const handleToggleCloseMonth = async () => {
    if (!currentMonthStr) return;
    const action = isCurrentMonthClosed ? 'reopen-month' : 'close-month';
    const verb = isCurrentMonthClosed ? 'Reopen' : 'Close';
    if (!window.confirm(`${verb} ${currentMonthStr}?\n\n${isCurrentMonthClosed
      ? 'Future Drive scans will start syncing this month again. Any invoices parked while it was closed will be re-processed on the next scan.'
      : 'Future Drive scans will not add or update transactions dated in this month until you reopen it.'}`)) return;
    setTogglingMonth(true);
    try {
      await axios.post(`${BACKEND_URL}/api/household/${configId}/${action}`,
        { month: currentMonthStr },
        { withCredentials: true }
      );
      toast.success(isCurrentMonthClosed ? 'Month reopened' : 'Month finalized');
      await loadOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to ${verb.toLowerCase()} month`);
    } finally {
      setTogglingMonth(false);
    }
  };

  // ---- HELPERS ----
  const fmtDate = (s) => { if (!s) return '-'; return new Date(s + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); };
  const shortDesc = (d) => { if (!d) return '-'; const i = d.split(',').map(s => s.trim()); return i.length <= 2 ? d : `${i.slice(0,2).join(', ')} +${i.length-2} more`; };
  const fmt = (n) => n != null ? parseFloat(n).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '-';
  const dateRangeLabel = () => {
    if (!data) return '';
    const s = new Date(data.start_date + 'T00:00:00'), e = new Date(data.end_date + 'T00:00:00');
    e.setDate(e.getDate() - 1);
    return period === 'weekly'
      ? `${s.toLocaleDateString('en-IN',{day:'numeric',month:'short'})} - ${e.toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}`
      : s.toLocaleDateString('en-IN',{month:'long',year:'numeric'});
  };

  // Filter orders by selected member (member view)
  const filteredOrders = (() => {
    const orders = data?.orders || [];
    if (viewMode !== 'member' || !selectedMember) return orders;
    return orders
      .map(o => {
        const share = (o.member_shares || []).find(ms => ms.member_id === selectedMember);
        if (!share || share.amount === 0) return null;
        return { ...o, _memberAmount: share.amount };
      })
      .filter(Boolean);
  })();

  const memberViewTotal = filteredOrders.reduce((s, o) => s + (o._memberAmount || 0), 0);

  return (
    <AppLayout user={user} activeNav="/households">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        {/* Header */}
        <div className="mb-6">
          <button onClick={() => navigate('/households')} className="flex items-center gap-1 text-sm text-stone-400 hover:text-sage-600 transition mb-3" data-testid="back-to-households">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-heading font-extrabold text-stone-900" data-testid="household-title">{data?.config?.household_name || 'Loading...'}</h2>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <button onClick={() => navigateDate('prev')} className={`text-stone-400 hover:text-stone-600 ${period === 'pending' ? 'invisible' : ''}`} data-testid="date-prev"><ChevronLeft className="w-4 h-4" /></button>
                <span className="text-sm text-stone-500">{period === 'pending' ? 'All pending bills · last 12 months' : dateRangeLabel()}</span>
                <button onClick={() => navigateDate('next')} className={`text-stone-400 hover:text-stone-600 ${period === 'pending' ? 'invisible' : ''}`} data-testid="date-next"><ChevronRight className="w-4 h-4" /></button>
                {period === 'monthly' && isCurrentMonthClosed && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-full text-[10px] font-bold uppercase tracking-wider" data-testid="finalized-badge">
                    <Lock className="w-3 h-3" /> Finalized
                  </span>
                )}
                {period === 'monthly' && isOwner && (
                  <button
                    onClick={handleToggleCloseMonth}
                    disabled={togglingMonth}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition disabled:opacity-50 ${
                      isCurrentMonthClosed
                        ? 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'
                        : 'bg-white border-amber-200 text-amber-700 hover:bg-amber-50'
                    }`}
                    data-testid={isCurrentMonthClosed ? 'reopen-month-button' : 'close-month-button'}
                    title={isCurrentMonthClosed ? 'Reopen this month for Drive sync' : 'Close this month — Drive scans will skip invoices dated here'}
                  >
                    {isCurrentMonthClosed
                      ? (<><Unlock className="w-3 h-3" /> Reopen month</>)
                      : (<><Lock className="w-3 h-3" /> Close month</>)}
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex bg-stone-100 rounded-full p-0.5">
                <button onClick={() => handlePeriodChange('weekly')} className={`px-4 py-1.5 text-sm font-medium rounded-full transition ${period === 'weekly' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-400'}`} data-testid="period-weekly">Weekly</button>
                <button onClick={() => handlePeriodChange('monthly')} className={`px-4 py-1.5 text-sm font-medium rounded-full transition ${period === 'monthly' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-400'}`} data-testid="period-monthly">Monthly</button>
                <button onClick={() => handlePeriodChange('pending')} className={`px-4 py-1.5 text-sm font-medium rounded-full transition flex items-center gap-1 ${period === 'pending' ? 'bg-white text-red-600 shadow-sm' : 'text-stone-400'}`} data-testid="period-pending">
                  Pending
                  {period !== 'pending' && pendingData?.total_pending > 0 && (
                    <span className="bg-red-500 text-white text-[8px] font-bold px-1 py-px rounded-full leading-none">!</span>
                  )}
                </button>
              </div>
              <button onClick={openAdd} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#4F6F52] to-[#5a7a5e] hover:from-[#3f5942] hover:to-[#4F6F52] rounded-xl shadow-md shadow-[#4F6F52]/20 transition" data-testid="add-transaction-button">
                <Plus className="w-4 h-4" /> Add
              </button>
              {splittingEnabled && members.length > 0 ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="flex items-center gap-1.5 px-3 py-2 bg-white rounded-xl border border-stone-200 text-sm font-medium text-stone-600 hover:bg-stone-50 shadow-sm transition" data-testid="share-popover-trigger">
                      <Share2 className="w-4 h-4" /> Share
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-72 p-2 rounded-xl">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-stone-400 px-2 pt-1 pb-1">Household</div>
                    <button onClick={handleShareHousehold} disabled={sharing} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-stone-100 text-left" data-testid="share-household">
                      <Users className="w-4 h-4 text-stone-500" />
                      <span className="flex-1 text-sm text-stone-700">Full household view</span>
                      <Copy className="w-3.5 h-3.5 text-stone-400" />
                    </button>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-stone-400 px-2 pt-2 pb-1 mt-1 border-t border-stone-100">Per Member</div>
                    {members.map(m => (
                      <div key={m.id} className="flex items-center gap-1 px-1">
                        <button onClick={() => handleShareMember(m.id)} disabled={sharing} className="flex-1 flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-stone-100 text-left" data-testid={`share-member-${m.id}`}>
                          <div className="w-5 h-5 rounded-full flex items-center justify-center text-white font-bold text-[9px]" style={{ background: m.color }}>{m.name[0]?.toUpperCase()}</div>
                          <span className="flex-1 text-sm text-stone-700 truncate">{m.name}'s view</span>
                          <Copy className="w-3.5 h-3.5 text-stone-400" />
                        </button>
                        <button onClick={() => handleShareMemberPending(m.id)} disabled={sharing} className="px-2 py-1.5 rounded-lg hover:bg-red-50 text-red-500 text-[10px] font-semibold flex items-center gap-1" title={`Copy ${m.name}'s pending bills link`} data-testid={`share-member-pending-${m.id}`}>
                          <AlertCircle className="w-3 h-3" /> Pending
                        </button>
                      </div>
                    ))}
                  </PopoverContent>
                </Popover>
              ) : shareUrl ? (
                <button onClick={() => copyToClipboard(`${shareUrl}${periodSuffix()}`)} className="flex items-center gap-1.5 px-3 py-2 bg-white rounded-xl border border-stone-200 text-sm font-medium text-stone-600 hover:bg-stone-50 shadow-sm transition" data-testid="copy-share-link"><Copy className="w-4 h-4" /> Link</button>
              ) : (
                <button onClick={handleShareHousehold} disabled={sharing} className="flex items-center gap-1.5 px-3 py-2 bg-white rounded-xl border border-stone-200 text-sm font-medium text-stone-600 hover:bg-stone-50 shadow-sm transition" data-testid="share-button"><Share2 className="w-4 h-4" /> Share</button>
              )}
            </div>
          </div>
        </div>

        {period === 'pending' ? (
          <div className="space-y-3">
            {/* Pending toolbar: Per Member toggle + Show paid */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              {splittingEnabled && memberTotals.length > 0 && (
                <div className="flex bg-stone-100 rounded-full p-0.5">
                  <button onClick={() => { setViewMode('household'); setSelectedMember(null); }} className={`px-4 py-1.5 text-xs font-semibold rounded-full transition ${viewMode === 'household' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-400'}`} data-testid="pending-view-household">Household</button>
                  <button onClick={() => setViewMode('member')} className={`px-4 py-1.5 text-xs font-semibold rounded-full transition ${viewMode === 'member' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-400'}`} data-testid="pending-view-member">Per Member</button>
                </div>
              )}
              <label className="inline-flex items-center gap-1.5 text-xs text-stone-600 cursor-pointer select-none ml-auto" data-testid="show-paid-toggle">
                <input type="checkbox" checked={showPaid} onChange={(e) => setShowPaid(e.target.checked)} className="accent-[#4F6F52]" />
                Show paid months
              </label>
            </div>
            {/* Quick member chips when in Per Member mode */}
            {splittingEnabled && viewMode === 'member' && memberTotals.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {memberTotals.map(m => (
                  <button
                    key={m.member_id}
                    onClick={() => setSelectedMember(selectedMember === m.member_id ? null : m.member_id)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition border ${selectedMember === m.member_id ? 'shadow-sm' : 'border-stone-200 bg-white hover:bg-stone-50'}`}
                    style={selectedMember === m.member_id ? { background: `${m.color}20`, color: m.color, borderColor: `${m.color}55` } : {}}
                    data-testid={`pending-member-chip-${m.member_id}`}
                  >
                    <span className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[8px] font-bold" style={{ background: m.color }}>{m.name[0]?.toUpperCase()}</span>
                    {m.name}
                  </button>
                ))}
              </div>
            )}
            {viewMode === 'member' && !selectedMember ? (
              <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-8 text-center text-sm text-stone-500">
                Pick a member chip above to see their pending bills.
              </div>
            ) : pendingLoading ? (
              <div className="bg-white rounded-2xl shadow-[0_4px_12px_rgba(0,0,0,0.06)] p-12 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#4F6F52] border-t-transparent mx-auto"></div>
              </div>
            ) : (
              <PendingOrdersList
                data={pendingData}
                viewer={pendingData?.viewer}
                onMarkMonth={handleMarkMonth}
                markingKey={markingKey}
                canMark={true}
                splittingEnabled={splittingEnabled}
              />
            )}
          </div>
        ) : (
        <>
        {/* Member summary + view toggle */}
        {splittingEnabled && memberTotals.length > 0 && (
          <div className="mb-5 space-y-3" data-testid="member-summary">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex bg-stone-100 rounded-full p-0.5">
                <button onClick={() => { setViewMode('household'); setSelectedMember(null); }} className={`px-4 py-1.5 text-xs font-semibold rounded-full transition ${viewMode === 'household' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-400'}`} data-testid="view-household">Household</button>
                <button onClick={() => setViewMode('member')} className={`px-4 py-1.5 text-xs font-semibold rounded-full transition ${viewMode === 'member' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-400'}`} data-testid="view-member">Per Member</button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {memberTotals.map(m => (
                <button
                  key={m.member_id}
                  onClick={() => { if (viewMode === 'member') setSelectedMember(selectedMember === m.member_id ? null : m.member_id); }}
                  className={`rounded-2xl p-4 text-left transition ${viewMode === 'member' && selectedMember === m.member_id ? 'ring-2 ring-offset-1 shadow-md' : 'hover:shadow-sm'} ${viewMode !== 'member' ? 'cursor-default' : 'cursor-pointer'}`}
                  style={{
                    background: `linear-gradient(135deg, ${m.color}18, ${m.color}08)`,
                    borderLeft: `3px solid ${m.color}`,
                    ...(viewMode === 'member' && selectedMember === m.member_id ? { '--tw-ring-color': m.color } : {})
                  }}
                  data-testid={`member-card-${m.member_id}`}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-[10px]" style={{ background: m.color }}>{m.name[0]?.toUpperCase()}</div>
                    <span className="text-xs font-semibold text-stone-700 truncate">{m.name}</span>
                  </div>
                  <div className="text-xl font-heading font-extrabold text-stone-900 tabular-nums">₹{fmt(m.amount)}</div>
                </button>
              ))}
            </div>
            {viewMode === 'member' && !selectedMember && (
              <p className="text-xs text-stone-400 italic">Select a member card above to view their transactions.</p>
            )}
          </div>
        )}

        {/* Orders Table */}
        <div className="bg-white rounded-2xl shadow-[0_4px_12px_rgba(0,0,0,0.06)] overflow-hidden">
          {loading ? (
            <div className="text-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#4F6F52] border-t-transparent mx-auto"></div></div>
          ) : !filteredOrders.length ? (
            <div className="text-center py-16">
              <Calendar className="w-10 h-10 text-stone-200 mx-auto mb-2" />
              <p className="text-stone-500 text-sm">
                {viewMode === 'member' && selectedMember ? 'No transactions for this member in this period' : 'No orders for this period'}
              </p>
              {viewMode !== 'member' && (
                <button onClick={openAdd} className="mt-3 text-sm text-[#4F6F52] font-medium hover:underline">Add a transaction</button>
              )}
            </div>
          ) : (
            <>
            <table className="hidden sm:table w-full table-fixed" data-testid="orders-table">
              <thead>
                <tr className="border-b border-stone-100">
                  <th className="w-[72px] px-4 py-3 text-left text-[10px] font-bold text-stone-400 uppercase tracking-widest">Date</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-stone-400 uppercase tracking-widest">Item</th>
                  <th className="w-[90px] px-4 py-3 text-right text-[10px] font-bold text-stone-400 uppercase tracking-widest">Amount</th>
                  <th className="w-[90px] px-4 py-3 text-right text-[10px] font-bold text-stone-400 uppercase tracking-widest">{viewMode === 'member' && selectedMember ? 'Share' : 'Deducted'}</th>
                  <th className="w-[90px] px-4 py-3 text-right text-[10px] font-bold text-stone-400 uppercase tracking-widest">{viewMode === 'member' && selectedMember ? '' : 'Final'}</th>
                  <th className="w-[60px] px-2 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order, idx) => (
                  <tr key={order.log_id} className={`border-b border-stone-50 hover:bg-stone-50/60 transition ${idx % 2 !== 0 ? 'bg-stone-50/30' : ''}`} data-testid={`order-row-${order.log_id}`}>
                    <td className="px-4 py-3 text-sm text-stone-700 font-medium">{fmtDate(order.date)}</td>
                    <td className="px-4 py-3 truncate">
                      <div className="flex items-center gap-1">
                        <p className="text-sm font-semibold text-stone-800 truncate">{order.category || order.vendor_name || '-'}</p>
                        {order.receipt_link && (
                          <a href={order.receipt_link} target="_blank" rel="noopener noreferrer" className="text-[#4F6F52] hover:text-[#3f5942] flex-shrink-0">
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                      <DescriptionCell description={order.description} />

                      {splittingEnabled && order.split_note && (
                        <p className="text-[10px] text-stone-500 mt-0.5 italic leading-snug" data-testid={`order-split-note-${order.log_id}`}>{order.split_note}</p>
                      )}
                      {splittingEnabled && viewMode === 'household' && (order.member_shares || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {order.member_shares.filter(ms => ms.amount > 0).map(ms => (
                            <span key={ms.member_id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold" style={{ background: `${ms.color}20`, color: ms.color }}>
                              {ms.name} ₹{fmt(ms.amount)}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-stone-700 text-right font-medium tabular-nums">{order.amount ? `₹${fmt(order.amount)}` : '-'}</td>
                    {viewMode === 'member' && selectedMember ? (
                      <td className="px-4 py-3 text-sm text-stone-900 text-right font-bold tabular-nums">₹{fmt(order._memberAmount)}</td>
                    ) : (
                      <td className="px-4 py-3 text-right">
                        <span className={`text-sm tabular-nums inline-flex items-center gap-1 ${order.deduction > 0 ? 'text-red-600 font-semibold' : 'text-stone-300'}`}>
                          {order.deduction > 0 ? `-₹${fmt(order.deduction)}` : '-'}
                          {order.notes && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button className={`p-0.5 rounded-full transition ${order.deduction > 0 ? 'hover:bg-red-100 text-red-400 hover:text-red-600' : 'hover:bg-blue-100 text-blue-400 hover:text-blue-600'}`} data-testid={`info-${order.log_id}`}>
                                  <Info className="w-3 h-3" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent side="top" className="w-auto max-w-[240px] p-3 rounded-xl text-xs text-stone-600">
                                {order.notes}
                              </PopoverContent>
                            </Popover>
                          )}
                        </span>
                      </td>
                    )}
                    {viewMode === 'member' && selectedMember ? (
                      <td className="px-4 py-3"></td>
                    ) : (
                      <td className="px-4 py-3 text-sm text-stone-900 text-right font-bold tabular-nums">₹{fmt(order.final_amount)}</td>
                    )}
                    <td className="px-2 py-3">
                      <div className="flex items-center justify-center gap-0.5">
                        <button onClick={() => openEdit(order)} className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-[#4F6F52] transition" data-testid={`edit-btn-${order.log_id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => deleteOrder(order.log_id)} className="p-1.5 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500 transition" data-testid={`delete-btn-${order.log_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-stone-200 bg-stone-50/60">
                  <td className="px-4 py-3.5 text-sm font-bold text-stone-700" colSpan={2}>Total</td>
                  <td className="px-4 py-3.5 text-sm text-stone-700 text-right font-bold tabular-nums">₹{fmt(data.total_amount)}</td>
                  {viewMode === 'member' && selectedMember ? (
                    <>
                      <td className="px-4 py-3.5 text-sm text-stone-900 text-right font-extrabold tabular-nums">₹{fmt(memberViewTotal)}</td>
                      <td></td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3.5 text-sm text-right font-bold tabular-nums text-red-600">{data.total_deductions > 0 ? `-₹${fmt(data.total_deductions)}` : '-'}</td>
                      <td className="px-4 py-3.5 text-sm text-stone-900 text-right font-extrabold tabular-nums">₹{fmt(data.total_final)}</td>
                    </>
                  )}
                  <td></td>
                </tr>
              </tfoot>
            </table>

            {/* Mobile cards */}
            <div className="sm:hidden divide-y divide-stone-100" data-testid="orders-mobile-list">
              {filteredOrders.map((order) => {
                const isMemberView = viewMode === 'member' && selectedMember;
                return (
                  <div key={order.log_id} className="px-4 py-3" data-testid={`order-row-mobile-${order.log_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <p className="text-sm font-semibold text-stone-900 truncate">{order.category || order.vendor_name || '-'}</p>
                          {order.receipt_link && (
                            <a href={order.receipt_link} target="_blank" rel="noopener noreferrer" className="text-[#4F6F52] flex-shrink-0">
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        <p className="text-[10px] text-stone-400 font-medium">{fmtDate(order.date)}</p>
                        <DescriptionCell description={order.description} className="text-[10px]" mobileMaxLen={40} />
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-heading font-extrabold text-stone-900 tabular-nums">
                          ₹{fmt(isMemberView ? order._memberAmount : order.final_amount)}
                        </div>
                        {!isMemberView && (
                          <div className="text-[10px] text-stone-500 tabular-nums mt-0.5">
                            {order.amount ? `₹${fmt(order.amount)}` : ''}
                            {order.deduction > 0 && (
                              <span className="inline-flex items-center gap-0.5 ml-1 text-red-600 font-semibold">
                                −₹{fmt(order.deduction)}
                                {order.notes && (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button className="p-0.5 rounded-full text-red-400 hover:text-red-600" data-testid={`info-mobile-${order.log_id}`}>
                                        <Info className="w-2.5 h-2.5" />
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent side="top" className="w-auto max-w-[220px] p-2.5 rounded-xl text-[11px] text-stone-600">
                                      {order.notes}
                                    </PopoverContent>
                                  </Popover>
                                )}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {splittingEnabled && order.split_note && (
                      <p className="text-[10px] text-stone-500 mt-1 italic leading-snug">{order.split_note}</p>
                    )}
                    {splittingEnabled && viewMode === 'household' && (order.member_shares || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {order.member_shares.filter(ms => ms.amount > 0).map(ms => (
                          <span key={ms.member_id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold" style={{ background: `${ms.color}20`, color: ms.color }}>
                            {ms.name} ₹{fmt(ms.amount)}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-end gap-1 mt-1.5">
                      <button onClick={() => openEdit(order)} className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-[#4F6F52]" data-testid={`edit-btn-mobile-${order.log_id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => deleteOrder(order.log_id)} className="p-1.5 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500" data-testid={`delete-btn-mobile-${order.log_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                );
              })}
              <div className="px-4 py-3 bg-stone-50/70 border-t border-stone-200 flex items-center justify-between" data-testid="orders-mobile-total">
                <span className="text-xs font-bold text-stone-700">Total</span>
                <div className="text-right">
                  <div className="text-base font-heading font-extrabold text-stone-900 tabular-nums">
                    ₹{fmt(viewMode === 'member' && selectedMember ? memberViewTotal : data.total_final)}
                  </div>
                  {!(viewMode === 'member' && selectedMember) && (
                    <div className="text-[10px] text-stone-500 tabular-nums">
                      ₹{fmt(data.total_amount)}
                      {data.total_deductions > 0 && <span className="text-red-600 font-semibold ml-1">−₹{fmt(data.total_deductions)}</span>}
                    </div>
                  )}
                </div>
              </div>
            </div>
            </>
          )}
        </div>
        </>
        )}
      </motion.div>

      <TransactionModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        mode={modalMode}
        form={form}
        setForm={setForm}
        lineItems={lineItems}
        setLineItems={setLineItems}
        members={members}
        splittingEnabled={splittingEnabled}
        saving={saving}
        onSave={handleSave}
      />
    </AppLayout>
  );
};

export default HouseholdDashboard;
